// todo: this file is an abomination and it should be rewritten
import { useEffect, useMemo, useState } from 'react';
import { useAlert } from 'react-alert';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';
import I18n from 'i18n-js';

import { fetchCountryIfNeeded } from '@lootboy/lib/actions/CountryActions';
import {
  createBundlePurchase,
  fetchLootIfNeeded,
} from '@lootboy/lib/actions/LootActions';
import {
  createPurchase,
  setLastBundleAmount,
  setLastPaymentMethod,
} from '@lootboy/lib/actions/PurchaseActions';
import { getAvailability } from '@lootboy/lib/api/loots';
import { CURRENCIES } from '@lootboy/lib/constants';
import { LOOTTICKET } from '@lootboy/lib/payments';
import { createLootDetailTagSelector } from '@lootboy/lib/selectors';
import { getSupportedBundles } from '@lootboy/lib/utils/bundles';
import {
  isFutureAndTruthy,
  isPastAndTruthy,
} from '@lootboy/lib/utils/dateAndTime';

import Alert from '~/components/Alert';

import { BUNDLE_BONUSES, INTERNAL_CURRENCIES } from '~/constants/payments';
import { useDocumentTitle } from '~/hooks/useDocumentTitle';
import {
  authSelector,
  languageSelector,
  loggedSelector,
  lootsSelector,
  purchasesSelector,
  userSelector,
} from '~/redux/selectors';
import { useNumberParser } from '~/utils';

import InfoMessagePlain from './InfoMessage';

const getLoot = (loots, urlAliasOrId) =>
  Object.values(loots).find((loot) => loot.urlAlias === urlAliasOrId) ||
  loots[urlAliasOrId] ||
  {};

export const SIGNUM_TYPE = 'signum';

export const selector = createSelector(
  lootsSelector,
  purchasesSelector,
  (_, { urlAliasOrId }) => urlAliasOrId,
  (loots, purchases, urlAliasOrId) => {
    const {
      fetchingInProgressSince: fetching,
      lastSuccessfulFetch,
      _id,
      ...lootRest
    } = getLoot(loots, urlAliasOrId);

    const loading = !!fetching || !lastSuccessfulFetch;
    const purchased = !!purchases.find(
      (purchase) =>
        purchase.status === 'PAID' &&
        purchase.loot &&
        purchase.loot._id === _id,
    );

    return {
      loading,
      _id,
      lastSuccessfulFetch,
      purchased,
      ...lootRest,
    };
  },
);

export const getInfoMessage = ({
  availableFrom,
  availableUntil,
  disabled,
  repurchaseble,
  purchased,
  limitExceeded,
  limitMessage,
  isQuestLootpack,
  limited,
}) => {
  if (limitExceeded) {
    const defaultMessage =
      isQuestLootpack && !limited
        ? I18n.t('LootDetailView_purchase_button_wait_quest')
        : I18n.t('LootDetailView_purchase_button_wait');
    return {
      type: 'warn',
      message: limitMessage || defaultMessage,
    };
  }

  if (purchased && !repurchaseble) {
    return {
      type: 'success',
      message: I18n.t('Api_LootController_error_not_repurchaseable'),
    };
  }

  if (disabled) {
    return {
      type: 'warn',
      message: I18n.t('LootDetailView_purchase_button_disabled'),
    };
  }

  if (isFutureAndTruthy(availableFrom)) {
    return {
      type: 'info',
      message: I18n.t('Api_LootController_error_not_available_yet'),
    };
  }

  if (isPastAndTruthy(availableUntil)) {
    return {
      type: 'warn',
      message: I18n.t('Api_LootController_error_no_longer_available'),
    };
  }

  return null;
};

export const useSyncContent = ({ lootId, lastSuccessfulFetch }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const alert = useAlert();
  const language = useSelector(languageSelector);

  useEffect(() => {
    const syncContent = async () => {
      const country = await dispatch(fetchCountryIfNeeded());
      await dispatch(
        fetchLootIfNeeded(lootId, {
          platform: 'web',
          country,
        }),
      );
    };
    syncContent().catch((error) => {
      alert.error(
        <Alert
          body={error.message}
          title={I18n.t('common_error')}
        />,
      );
      if (!lastSuccessfulFetch) {
        history.push('/');
      }
    });
  }, [alert, dispatch, history, language, lastSuccessfulFetch, lootId]);
};

export const useLimitExceeded = ({
  repurchaseble,
  limited,
  currency,
  isQuestLootpack,
  _id,
}) => {
  const [limitObject, setLimitObject] = useState(null);

  const { apiUrl, token } = useSelector(authSelector);

  useEffect(() => {
    const getAvailabilityWrapped = async () => {
      if (
        apiUrl &&
        token &&
        repurchaseble &&
        (limited || !INTERNAL_CURRENCIES.includes(currency) || isQuestLootpack)
      ) {
        const { availability, limitMessage } = await getAvailability(
          apiUrl,
          token,
          _id,
        );
        setLimitObject({
          limitExceeded: !availability,
          limitMessage,
        });
      }
    };
    if (limitObject === null) {
      getAvailabilityWrapped().catch(() => {});
    }
  }, [
    apiUrl,
    token,
    repurchaseble,
    limited,
    limitObject,
    currency,
    isQuestLootpack,
    _id,
  ]);

  return limitObject;
};

export const getButtonSettings = ({
  isLoggedIn,
  price,
  ticketPrice,
  currency,
  lootcoinBalance,
  lootgemBalance,
  ticketBalance,
  paymentMethod,
  ticketPayment,
}) => {
  let title;
  let alertMessage;
  let disabled;
  let validateEmail;
  if (isLoggedIn && currency === 'LOOTCOIN' && price > lootcoinBalance) {
    title = I18n.t('LootDetailView_purchase_button_lootgem');
    alertMessage = I18n.t('LootDetailView_purchase_lootcoins_too_low');
    disabled = true;
  } else if (isLoggedIn && currency === 'LOOTGEM' && price > lootgemBalance) {
    title = I18n.t('LootDetailView_purchase_button_lootgem');
    alertMessage = I18n.t('LootDetailView_purchase_diamonds_too_low');
    disabled = true;
  } else if (isLoggedIn && ticketPayment && ticketPrice > ticketBalance) {
    title = I18n.t('LootDetailView_purchase_button_lootgem');
    alertMessage = I18n.t('LootDetailView_purchase_loottickets_too_low');
    disabled = true;
  } else if (INTERNAL_CURRENCIES.includes(currency)) {
    title = I18n.t('LootDetailView_purchase_button_lootgem');
  } else if (paymentMethod && paymentMethod.type === 'paymentwall') {
    validateEmail = true;
  }
  return {
    title,
    disabled,
    alertMessage,
    validateEmail,
  };
};

export const closeAndOpenPurchases = ({ purchasesList, history }) => {
  const [purchase, ...purchases] = purchasesList;

  if (purchase && purchase._id) {
    history.replace(`/purchases/${purchase._id}`, {
      remainingPurchases: purchases
        ? purchases
            .filter((p) => p.status === 'PAID' && !p.opened)
            .map((p) => p._id)
        : [],
    });
  }
};

export const usePriceLabel = ({
  ticketPayment,
  price,
  ticketPrice,
  currency,
}) => {
  const getParsedNumber = useNumberParser();

  if (currency === 'FREE') {
    return I18n.t('LootPrice_free');
  }
  if (ticketPayment) {
    return getParsedNumber(ticketPrice);
  }
  return getParsedNumber(price, currency);
};

const onPurchasePlain = async ({
  paymentMethod,
  lootId,
  dispatch,
  bundleSelected,
  currency,
  email,
  purchaseType,
  ticketPayment,
  shouldUseFallbackCurrency,
}) => {
  let purchases;
  const vendor =
    (paymentMethod && paymentMethod.type) || currency.toLowerCase();

  const vendorData = {};
  const paymentData = {};

  if (vendor === 'paymentwall') {
    paymentData.ps = paymentMethod.ps;
    paymentData.email = email;
  }
  if (vendor === 'skrill') {
    paymentData.paymentMethod = paymentMethod.paymentMethod;
  }

  // this flag should be checked for historic reasons,
  // it will probably never be fulfilled but it should remain
  if (purchaseType !== 'onetime') {
    throw new Error(I18n.t('PurchaseView_purchase_error_msg'));
  }
  if (ticketPayment) {
    ({ purchases } = await dispatch(
      createPurchase({
        lootId,
        type: 'EXCHANGE_TICKETS',
        application: 'web',
        bundle: bundleSelected,
      }),
    ));
  } else {
    purchases = await dispatch(
      createBundlePurchase(
        lootId,
        {
          application: 'web',
          vendor: shouldUseFallbackCurrency ? 'internal' : vendor,
          vendorData,
          paymentData,
          useFallback: shouldUseFallbackCurrency,
        },
        bundleSelected,
      ),
    );
  }

  if (currency !== 'FREE' && currency !== 'LOOTCOIN') {
    dispatch(setLastBundleAmount(bundleSelected));
    dispatch(setLastPaymentMethod(paymentMethod));
  }
  const [purchase] = purchases;

  if (purchase.status === 'PAID') {
    return purchases;
  }

  if (
    purchase.status === 'AUTHORIZATION_REQUIRED' &&
    purchase.authorizationUri
  ) {
    window.open(purchase.authorizationUri, '_self');
    return null;
  }
  if (vendor === 'paysafecard') {
    throw new Error(I18n.t('PurchaseView_paysafecard_error'));
  }

  throw new Error(`Unexpected purchase status "${purchase.status}".`);
};

const getDefaultBundle = (loot, initialBundle) => {
  const supportedBundles = getSupportedBundles(loot);
  if (supportedBundles.includes(initialBundle)) {
    return initialBundle;
  }
  return supportedBundles[0] || 'x1';
};

export const useLootPackHook = ({
  initialBundle,
  paymentMethod,
  changeShowSeasonTicketDialog,
} = {}) => {
  const { lootId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const alert = useAlert();
  const user = useSelector(userSelector);
  const isLoggedIn = useSelector(loggedSelector);
  const ticketPayment =
    paymentMethod && paymentMethod.type === LOOTTICKET.lootTicket.type;

  const [processing, setProcessing] = useState(false);

  const {
    _id,
    lastSuccessfulFetch,
    availableFrom,
    availableUntil,
    disabled,
    currency,
    repurchaseble,
    purchased,
    isQuestLootpack,
    limited,
    bundleVariants,
    price: singlePrice,
    ticketPrice: singleTicketPrice,
    purchaseType,
    supportedBundles,
    headline,
    subHeadline,
    fallbackCurrency,
    fallbackPrice,
    ...rest
  } = useSelector((state) => selector(state, { urlAliasOrId: lootId }));

  const shouldUseFallbackCurrency =
    currency === CURRENCIES.AD_CURRENCY && !!fallbackCurrency;
  const finalCurrency = shouldUseFallbackCurrency ? fallbackCurrency : currency;

  const finalPrice = shouldUseFallbackCurrency ? fallbackPrice : singlePrice;

  const tagSelector = useMemo(
    () => createLootDetailTagSelector(rest?.tags, rest?.separatorColorScheme),
    [rest?.tags, rest?.separatorColorScheme],
  );

  const { lootTypeTag, separatorTag } = useSelector(tagSelector);

  let title = '';

  if (headline && subHeadline) {
    title = `${headline} ${subHeadline}`;
    if (tagSelector?.title) {
      title = `${title} ${separatorTag.title}`;
    }
  }

  useDocumentTitle(title);

  const [bundleSelected, setBundleSelected] = useState(
    getDefaultBundle(
      {
        bundleVariants,
        repurchaseble,
        supportedBundles,
      },
      initialBundle,
    ),
  );

  useEffect(() => {
    const defaultBundle = getDefaultBundle(
      {
        bundleVariants,
        repurchaseble,
        supportedBundles,
      },
      bundleSelected,
    );
    if (defaultBundle !== bundleSelected) {
      setBundleSelected(defaultBundle);
    }
  }, [supportedBundles, bundleVariants, repurchaseble, bundleSelected]);

  useSyncContent({
    lootId,
    lastSuccessfulFetch,
  });

  const { limitExceeded, limitMessage } =
    useLimitExceeded({
      repurchaseble,
      limited,
      currency: finalCurrency,
      isQuestLootpack,
      _id,
    }) || {};

  const bundles = useMemo(() => {
    return getSupportedBundles({
      bundleVariants,
      repurchaseble,
      supportedBundles,
    });
  }, [bundleVariants, repurchaseble, supportedBundles]);

  const { price = finalPrice, ticketPrice = singleTicketPrice } =
    (bundleSelected &&
      bundles.length > 1 &&
      bundleVariants &&
      bundleVariants[bundleSelected]) ||
    {};

  const priceLabel = usePriceLabel({
    price,
    ticketPrice,
    ticketPayment,
    currency: finalCurrency,
  });

  const infoMessage = getInfoMessage({
    availableUntil,
    availableFrom,
    currency: finalCurrency,
    repurchaseble,
    disabled,
    purchased,
    isQuestLootpack,
    limited,
    limitExceeded,
    limitMessage,
  });

  const InfoMessage = infoMessage
    ? () => (
        <InfoMessagePlain type={infoMessage.type}>
          {infoMessage.message}
        </InfoMessagePlain>
      )
    : null;

  const {
    lootcoinBalance,
    lootgemBalance,
    ticketBalance,
    email: userEmail,
  } = user || {};

  const buttonSettings = getButtonSettings({
    isLoggedIn,
    price,
    ticketPrice,
    currency: finalCurrency,
    lootcoinBalance,
    lootgemBalance,
    ticketBalance,
    paymentMethod,
  });

  const isInternal =
    INTERNAL_CURRENCIES.includes(finalCurrency) || ticketPayment;

  const bonuses = isInternal ? {} : BUNDLE_BONUSES;

  const onPurchase = (email) => {
    if (processing) {
      return;
    }
    setProcessing(true);
    onPurchasePlain({
      paymentMethod,
      dispatch,
      lootId: _id,
      currency,
      purchaseType,
      bundleSelected,
      email,
      ticketPayment,
      shouldUseFallbackCurrency,
    })
      .then((purchasesList) => {
        if (purchasesList) {
          closeAndOpenPurchases({
            purchasesList,
            history,
          });
        } else {
          setProcessing(false);
        }
      })
      .catch((err) => {
        setProcessing(false);
        if (
          err.messageId === 'season_ticket_popup_lootpack_description' &&
          changeShowSeasonTicketDialog
        ) {
          changeShowSeasonTicketDialog.setTrue();
        } else {
          alert.error(
            <Alert
              title={I18n.t('common_error')}
              body={err.message}
            />,
          );
        }
      });
  };

  const newBalance = ticketPayment
    ? ticketBalance - ticketPrice
    : finalCurrency && user[`${finalCurrency.toLowerCase()}Balance`] - price;

  const canPurchase = !isInternal || newBalance >= 0;

  return {
    ...rest,
    _id,
    InfoMessage,
    bundleSelected,
    setBundleSelected,
    price,
    ticketPrice,
    bundles,
    buttonSettings,
    priceLabel,
    bonuses,
    isLoggedIn,
    currency: finalCurrency,
    newBalance,
    userEmail,
    onPurchase,
    processing,
    ticketBalance,
    lootId: _id,
    availableUntil,
    availableFrom,
    ticketPayment,
    canPurchase,
    lootTypeTag,
    separatorTag,
    headline,
    subHeadline,
  };
};
